import { useEffect } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useForm, Controller } from 'react-hook-form'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material'
import dayjs from 'dayjs'

import { Pregnancy } from '@/core/models/pregnancy.model'
import { useCreatePregnancyMutation, useUpdatePregnancyMutation } from '@/core/services/pregnancy'

type PregnancyDialogProps = {
  open: boolean
  onClose: () => void
  patientId: string
  pregnancy?: Pregnancy | null
}

type FormData = {
  lmpDate: string
  expectedDueDate: string
  status: 'active' | 'delivered' | 'ended'
  notes: string
}

export function PregnancyDialog({ open, onClose, patientId, pregnancy }: PregnancyDialogProps) {
  useLingui()

  const [createPregnancy, { isLoading: isCreating, error: createError }] = useCreatePregnancyMutation()
  const [updatePregnancy, { isLoading: isUpdating, error: updateError }] = useUpdatePregnancyMutation()

  const isEdit = !!pregnancy
  const isSubmitting = isCreating || isUpdating
  const error = createError || updateError

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormData>({
    defaultValues: {
      lmpDate: '',
      expectedDueDate: '',
      status: 'active',
      notes: '',
    },
  })

  const watchedLmpDate = watch('lmpDate')

  // Auto-compute expected due date from LMP + 280 days
  useEffect(() => {
    if (watchedLmpDate && !isEdit) {
      const dueDate = dayjs(watchedLmpDate).add(280, 'day').format('YYYY-MM-DD')
      setValue('expectedDueDate', dueDate)
    }
  }, [watchedLmpDate, setValue, isEdit])

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (pregnancy) {
        reset({
          lmpDate: dayjs(pregnancy.lmpDate).format('YYYY-MM-DD'),
          expectedDueDate: pregnancy.expectedDueDate ? dayjs(pregnancy.expectedDueDate).format('YYYY-MM-DD') : '',
          status: pregnancy.status,
          notes: pregnancy.notes || '',
        })
      } else {
        reset({
          lmpDate: '',
          expectedDueDate: '',
          status: 'active',
          notes: '',
        })
      }
    }
  }, [open, pregnancy, reset])

  const onSubmit = async (data: FormData) => {
    try {
      if (isEdit && pregnancy) {
        await updatePregnancy({
          id: pregnancy._id,
          body: {
            lmpDate: data.lmpDate,
            expectedDueDate: data.expectedDueDate || undefined,
            status: data.status,
            notes: data.notes || undefined,
          },
        }).unwrap()
      } else {
        await createPregnancy({
          patient: patientId,
          lmpDate: data.lmpDate,
          expectedDueDate: data.expectedDueDate || undefined,
          status: data.status,
          notes: data.notes || undefined,
        }).unwrap()
      }
      onClose()
    } catch {
      // Error handled by RTK Query
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{isEdit ? t`Edit Pregnancy` : t`Add Pregnancy`}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t`Error saving data`}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="lmpDate"
                control={control}
                rules={{ required: t`This field is required` }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="date"
                    label={t`Last Menstrual Period (LMP)`}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="expectedDueDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    label={t`Expected Due Date`}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={t`Auto-calculated from LMP + 280 days`}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label={t`Status`} fullWidth>
                    <MenuItem value="active">{t`Active`}</MenuItem>
                    <MenuItem value="delivered">{t`Delivered`}</MenuItem>
                    <MenuItem value="ended">{t`Ended`}</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => <TextField {...field} label={t`Notes`} fullWidth multiline rows={2} />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t`Cancel`}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={20} /> : isEdit ? t`Update` : t`Add`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
